/**
 * Wall-clock arithmetic in an IANA time zone, DST-correct without an offset
 * table: every offset is read from `Intl` at the instant in question. The
 * night window (`nightWindowState`) and the routing `depart_at`
 * (`departAtString`) are computed in the city's zone with it.
 */

const partsFormatters = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = partsFormatters.get(timeZone)
  if (fmt === undefined) {
    fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    partsFormatters.set(timeZone, fmt)
  }
  return fmt
}

/** The wall clock of `timeZone` at an instant (`hour` 0–23). */
export interface ZonedParts {
  readonly year: number
  readonly month: number
  readonly day: number
  readonly hour: number
  readonly minute: number
  readonly second: number
}

export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = formatter(timeZone).formatToParts(date)
  const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value)
  // en-GB with hour12:false renders midnight as "24" on some engines.
  const hour = get('hour') === 24 ? 0 : get('hour')
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    second: get('second'),
  }
}

/** The UTC offset (ms) of `timeZone` in effect at an absolute instant. */
export function zoneOffsetMs(utcTs: number, timeZone: string): number {
  const p = zonedParts(new Date(utcTs), timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  // Drop the instant's milliseconds: the wall clock has none.
  return asUtc - (utcTs - (((utcTs % 1000) + 1000) % 1000))
}

/**
 * The instant of the wall clock `year-month-day 00:00` plus `minute` minutes
 * in `timeZone` (`minute` may run past midnight: 390 = 06:30). The offset is
 * resolved at the candidate instant and, when a DST change lies between,
 * once more, so either side of the change maps to the right moment.
 */
export function zonedWallClockToInstant(
  year: number,
  month: number,
  day: number,
  minute: number,
  timeZone: string,
): number {
  const asIfUtc = Date.UTC(year, month - 1, day, 0, minute, 0)
  const offset = zoneOffsetMs(asIfUtc, timeZone)
  const ts = asIfUtc - offset
  const offset2 = zoneOffsetMs(ts, timeZone)
  return offset2 === offset ? ts : asIfUtc - offset2
}
